import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { PhotoManager } from "component/shared/photo-manager";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	fetchActiveListings,
	fetchActiveListingModify,
	updateActiveListingModify,
	activeListingModifyCleared,
} from "redux/slice/active-listings";
import { ActiveListingResource } from "api/resource/active-listing";
import { UpdateActiveListingModifyResource } from "api/resource/active-listing-modify";
import { coldFusionReplatform } from "shared/env";
import * as paths from "routes/paths";
import { ActiveListingForm } from "./active-listing-form";

interface Props extends PageProps, Router.RouteComponentProps {}

const adminTitle = "Active Listings";
const agentTitle = "Manage Listings";

// CF Address line 2 = GetToken(StreetNumber,1,".") + " " + UCase(StreetName): street number truncated
// at the first ".", street name upper-cased. Matches the list cell (listings-active/index.tsx).
const streetLine = (listing: ActiveListingResource): string => {
	const streetNo = (listing.streetNumber || "").split(".")[0];
	return `${streetNo} ${(listing.streetName || "").toUpperCase()}`.trim();
};

// CF header price = "$" + NumberFormat(ListPrice) (listingsmodify_output_1.cfm:51); matches the list cell.
const formatPrice = (price: number | null): string => (price == null ? "" : `$${price.toLocaleString()}`);

const ActiveEditBase: React.FC<Props> = ({ user, history, match }) => {
	const dispatch = useDispatch();
	const params = match.params as { boardId?: string; listingNumber?: string };
	const boardId = Number(params.boardId);
	const listingNumber = params.listingNumber || "";

	const loaded = useSelector((state: RootState) => state.activeListings.loaded);
	const listings = useSelector((state: RootState) => state.activeListings.listings);
	const modifying = useSelector((state: RootState) => state.activeListings.modifying);
	const modifyLoading = useSelector((state: RootState) => state.activeListings.modifyLoading);

	const title = user.admin ? adminTitle : agentTitle;

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [tab, setTab] = useState(0);

	// Header context (address/city/price/listingPageUrl) lives on the list row, not the modify row, so a
	// direct load / refresh of this route needs the list fetched to render the summary. Navigating in from
	// the list already has it in state; this covers deep links.
	useEffect(() => {
		if (!loaded) {
			dispatch(fetchActiveListings({ admin: user.admin, agentId: user.agentId }) as any);
		}
	}, [loaded, dispatch, user.admin, user.agentId]);

	// Load the AllDesc override row on mount; clear on unmount so a stale row never flashes into the next
	// edit before its own fetch resolves.
	useEffect(() => {
		dispatch(fetchActiveListingModify({ listingNumber, boardId }) as any);
		return () => {
			dispatch(activeListingModifyCleared());
		};
	}, [dispatch, listingNumber, boardId]);

	const editingRow = listings.find(
		(listing) => listing.listingNumber === listingNumber && listing.boardId === boardId
	);

	const goToList = (message?: string) =>
		history.push(
			// Capital `Message` — matches the active list's success-banner reader (index.tsx).
			message ? `${paths.listingsActive}?Message=${encodeURIComponent(message)}` : paths.listingsActive
		);

	const handleSave = async (body: UpdateActiveListingModifyResource) => {
		setSaving(true);
		setError(null);
		setSuccess(null);
		try {
			// .unwrap() so an API failure rejects here rather than resolving and navigating away.
			await dispatch(updateActiveListingModify({ listingNumber, boardId, body }) as any).unwrap();
			// MAX-3936: stay on the edit page and surface success inline instead of navigating back to
			// the list — matches CF (listingsupdate.cfm redirects back to ListingsModify.cfm with the
			// success message, not to the listing list).
			setSaving(false);
			setSuccess("Modifications Successful");
		} catch{
			setSaving(false);
			setError("Failed to save listing");
		}
	};

	const headerAddress = editingRow
		? [
				streetLine(editingRow),
				[(editingRow.city || "").toUpperCase(), editingRow.state].filter(Boolean).join(", "),
				editingRow.postalCode || "",
		  ]
				.filter(Boolean)
				.join(" , ")
		: "";

	return (
		<DashboardLayout
			permitted={user.admin || (user.type.agent && user.permissions.modifyListings)}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
		>
			{/* Address title + "$<ListPrice> | MLS #: <ListingNumber> | <PropertyType label>" summary,
			    with View Property Page (listingsmodify_output_1.cfm:50-67). Rendered once the list row is
			    available. Property type uses the app's standard label (propertyTypeLabel); CF's raw-code→
			    literal map isn't reproducible here since the resource carries no raw code. */}
			{editingRow && (
				<Mui.Box mb={2} display="flex" justifyContent="space-between" alignItems="flex-start">
					<div>
						<Mui.Typography variant="h6">{headerAddress}</Mui.Typography>
						<Mui.Typography variant="body2" color="textSecondary">
							{formatPrice(editingRow.listPrice)} | MLS #: {editingRow.listingNumber}
							{(editingRow.propertyTypeLabel || editingRow.propertyTypeDisplay) &&
								` | ${editingRow.propertyTypeLabel || editingRow.propertyTypeDisplay}`}
						</Mui.Typography>
					</div>
					{editingRow.listingPageUrl && (
						<Mui.Button
							variant="contained"
							component="a"
							href={editingRow.listingPageUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							View Property Page
						</Mui.Button>
					)}
				</Mui.Box>
			)}

			{/* CF's editor is a two-tab layout: Listing Information | Photos (MAX-3939). Both panels stay
			    mounted (hidden when inactive) so unsaved form edits survive a tab switch. */}
			<Mui.Tabs
				value={tab}
				onChange={(_e, value) => setTab(value)}
				indicatorColor="primary"
				textColor="primary"
			>
				<Mui.Tab label="Listing Information" />
				<Mui.Tab label="Photos" />
			</Mui.Tabs>

			<Mui.Box mt={2} hidden={tab !== 0}>
				{modifyLoading && !modifying ? (
					<FullPageCard>
						<Mui.Box display="flex" justifyContent="center" p={4}>
							<Mui.CircularProgress />
						</Mui.Box>
					</FullPageCard>
				) : (
					<ActiveListingForm
						// Remount per listing so the form re-initializes from the freshly loaded override row.
						key={`${boardId}-${listingNumber}`}
						listing={modifying}
						saving={saving}
						onSave={handleSave}
						onCancel={() => goToList()}
					/>
				)}
			</Mui.Box>

			<Mui.Box mt={2} hidden={tab !== 1}>
				<FullPageCard>
					<PhotoManager listingKey={{ listingNumber, boardId }} hasBoard inline />
				</FullPageCard>
			</Mui.Box>

			<Mui.Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
				<MuiAlert severity="error">
					<Mui.Typography>{error}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>

			<Mui.Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
				<MuiAlert severity="success">
					<Mui.Typography>{success}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ActiveEditPageBase: React.FC<Props> = (props) => {
	// Native edit route only exists under the replatform flag; otherwise CF owns the editor inside its own
	// iframe, so send deep links back to the list route (which renders the CF iframe when off).
	if (!coldFusionReplatform) {
		return <Router.Redirect to={paths.listingsActive} />;
	}
	return <ActiveEditBase {...props} />;
};

export const ActiveEditPage = ActiveEditPageBase;
